import Service, { service } from '@ember/service';
import {
  POWERBI_MAP,
  type PowerBiMap,
  type PowerBiPageRoute,
  type PowerBiReportRoute,
} from 'walden-fast/constants/power-bi';
import type {
  PowerBiEmbeddedTokenResponse,
  PowerBiPage,
  PowerBiPageDisplayName,
  PowerBiReport,
  PowerBiReportInternalCode,
  PowerBiReportPagesResponse,
} from 'walden-fast/types/fast-backend/power-bi';
import type FastApiService from './fast-api';
import type ImpersonateService from './impersonate';
import type UserService from './user';

export interface Pages {
  displayName: PowerBiPageDisplayName;
  externalUrl?: string;
  id: string;
  routeName?: PowerBiPageRoute;
}

export interface TokenReportAndPages {
  pages: Pages[];
  report: PowerBiReport;
  token: string;
}

export default class PowerBiService extends Service {
  @service('fast-api') declare fastApiService: FastApiService;
  @service('impersonate') declare impersonateService: ImpersonateService;
  @service('user') declare userService: UserService;

  expiration: Date | null = null;
  pagesPerReport: Record<string, PowerBiPage[]> = {};
  reports: PowerBiReport[] = [];
  token: string = '';

  private readonly TOKEN_CACHE_KEY = 'powerbi-token';
  private readonly PAGES_CACHE_KEY_PREFIX = 'powerbi-pages-';
  private readonly TOKEN_CACHE_DURATION = 60 * 60 * 1000; // 1 hour cache
  private readonly PAGES_CACHE_DURATION = 60 * 60 * 1000; // 1 hour cache for pages

  async fetchTokenAndReports(): Promise<void> {
    try {
      if (
        this.userService.needsToImpersonate() &&
        !this.impersonateService.getImpersonatingId()
      ) {
        return;
      }

      const response =
        await this.fastApiService.get<PowerBiEmbeddedTokenResponse>(
          '/power-bi/embedded-token',
          undefined,
          {
            key: this.TOKEN_CACHE_KEY,
            options: {
              duration: this.TOKEN_CACHE_DURATION,
              useImpersonationKey: true,
            },
          },
        );

      if (!response?.token) {
        throw new Error(`No token or reports fetched`);
      }

      this.expiration = new Date(response.expiration);
      this.reports = response.reports;
      this.token = response.token;
    } catch (error) {
      console.error('Error fetching embedded token:', error);
      throw error;
    }
  }

  isTokenValid(): boolean {
    return !!this.token && !!this.expiration && new Date() < this.expiration;
  }

  async fetchPagesForReport(report: PowerBiReport): Promise<void> {
    const cacheKey = `${this.PAGES_CACHE_KEY_PREFIX}${report.internalCode}`;

    try {
      const response =
        await this.fastApiService.get<PowerBiReportPagesResponse>(
          `/power-bi/report/${report.internalCode}/pages`,
          undefined,
          {
            key: cacheKey,
            options: {
              duration: this.PAGES_CACHE_DURATION,
              useImpersonationKey: true,
            },
          },
        );

      if (!response?.reportPages) {
        throw new Error(`Pages for "${report.internalCode}" not found`);
      }

      this.pagesPerReport[report.internalCode] = response.reportPages;
    } catch (error) {
      console.error('Error fetching pages for report');
      throw error;
    }
  }

  getMappedReportForRouteName(
    reportRoute: PowerBiReportRoute,
  ): PowerBiMap | undefined {
    return POWERBI_MAP[reportRoute];
  }

  async getReport(
    params:
      | { reportRoute: PowerBiReportRoute }
      | { reportInternalCode: PowerBiReportInternalCode },
  ): Promise<PowerBiReport | null> {
    if (!this.isTokenValid() || !this.reports.length) {
      await this.fetchTokenAndReports();
    }

    const reportInternalCode =
      'reportInternalCode' in params
        ? params.reportInternalCode
        : this.getMappedReportForRouteName(params.reportRoute)
            ?.reportInternalCode;
    const report = this.reports.find(
      (report) => report.internalCode === reportInternalCode,
    );

    if (!report) {
      console.warn('Report not found');
      return null;
    }

    return report;
  }

  async getPagesForReport(report: PowerBiReport): Promise<PowerBiPage[]> {
    if (!this.pagesPerReport[report.internalCode]) {
      await this.fetchPagesForReport(report);
    }

    const pages = this.pagesPerReport[report.internalCode];

    if (!pages?.length) {
      throw new Error('Pages not found for report');
    }

    return pages;
  }

  async getTokenReportAndPages(
    params:
      | { reportRoute: PowerBiReportRoute }
      | { reportInternalCode: PowerBiReportInternalCode },
  ): Promise<TokenReportAndPages | null> {
    if (
      this.userService.needsToImpersonate() &&
      !this.impersonateService.getImpersonatingId()
    ) {
      return null;
    }

    const report = await this.getReport(params);

    if (!report) {
      return null;
    }

    const pages = await this.getPagesForReport(report);

    if ('reportInternalCode' in params) {
      return { token: this.token, report, pages };
    }

    const mappedPages =
      this.getMappedReportForRouteName(params.reportRoute)?.pages.map(
        (page) => ({
          ...page,
          id: pages.find((p) => p.displayName === page.displayName)?.id || '',
        }),
      ) || [];

    return { token: this.token, report, pages: mappedPages };
  }
}

// Don't remove this declaration: this is what enables TypeScript to resolve
// this service using `Owner.lookup('service:power-bi')`, as well
// as to check when you pass the service name as an argument to the decorator,
// like `@service('power-bi') declare altName: PowerBiService;`.
declare module '@ember/service' {
  interface Registry {
    'power-bi': PowerBiService;
  }
}
